/**
 * Reusable error toast utility with contact information
 * Shows user-friendly error messages with options to contact support
 */

import { toast } from "@/hooks/use-toast"
import { Button } from "@/components/ui/button"
import { Mail, Twitter } from "lucide-react"

const SUPPORT_EMAIL = "support@planami.app"
const TWITTER_URL = "https://x.com/maryna_klokova"

interface ShowErrorToastOptions {
  title?: string
  description?: string
  error?: unknown
  context?: string // Additional context about what failed (e.g., "moodboard image")
}

/**
 * Shows an error toast with contact information
 * Reusable across the app for consistent error handling
 */
export function showErrorToast({
  title = "Something went wrong",
  description,
  error,
  context,
}: ShowErrorToastOptions = {}) {
  // Build error message
  let errorMessage = description
  if (!errorMessage && error) {
    if (error instanceof Error) {
      errorMessage = error.message
    } else if (typeof error === "string") {
      errorMessage = error
    } else {
      errorMessage = "An unexpected error occurred"
    }
  }
  if (!errorMessage) {
    errorMessage = "Please try again."
  }

  // Add context if provided
  const fullDescription = context
    ? `${context}: ${errorMessage}`
    : errorMessage

  // Create contact action buttons
  const emailSubject = context
    ? encodeURIComponent(`${context} Error`)
    : encodeURIComponent("Error Report")
  const emailBody = context
    ? encodeURIComponent(`Context: ${context}\n\nError: ${errorMessage}`)
    : encodeURIComponent(`Error: ${errorMessage}`)

  const descriptionContent = (
    <div className="flex flex-col gap-3">
      <span className="text-sm text-destructive">
        {fullDescription}
      </span>
      <div className="mt-1 border-t border-destructive pt-2 text-xs">
        <span className="mb-1 block font-medium text-[11px] uppercase tracking-wide text-destructive">
          Contact support
        </span>
        <div className="flex flex-wrap gap-1.5">
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-[11px] text-destructive hover:bg-destructive/10"
            onClick={() => {
              window.open(
                `mailto:${SUPPORT_EMAIL}?subject=${emailSubject}&body=${emailBody}`,
                "_blank"
              )
            }}
          >
            <Mail className="mr-1 h-3 w-3" />
            Email
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-[11px] text-destructive hover:bg-destructive/10"
            onClick={() => {
              window.open(TWITTER_URL, "_blank")
            }}
          >
            <Twitter className="mr-1 h-3 w-3" />
            X
          </Button>
        </div>
      </div>
    </div>
  )

  toast({
    variant: "default",
    title,
    description: descriptionContent,
    className: "border-destructive",
  })
}

